// Birthdays and other dates that come back every year (golf 9). Pure, shared by the worker and the client.

import { addDays } from "./time";

export interface YearlyDate {
  title: string;
  kind: "birthday" | "other";
  month: number;
  day: number;
  year: number | null;
  remind_days_before: number;
}

function isLeap(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** The date in a given year. 29 February falls on 28 February in a year without it (L126). */
export function dateInYear(d: { month: number; day: number }, year: number): string {
  const day = d.month === 2 && d.day === 29 && !isLeap(year) ? 28 : d.day;
  return `${year}-${String(d.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** The first time it comes, from a date on (that date included). */
export function nextOn(d: { month: number; day: number }, from: string): string {
  const year = Number(from.slice(0, 4));
  const thisYear = dateInYear(d, year);
  return thisYear >= from ? thisYear : dateInYear(d, year + 1);
}

/** Days from one date to another (both "YYYY-MM-DD"). */
export function daysUntil(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** The age someone turns on that day, if the year is known. */
export function ageOn(d: { year: number | null }, on: string): number | null {
  return d.year ? Number(on.slice(0, 4)) - d.year : null;
}

/** "Mam jarig (60)" for a birthday, the title itself for another date. */
export function dateLabel(d: YearlyDate, on: string): string {
  if (d.kind !== "birthday") return d.title;
  const age = ageOn(d, on);
  return age !== null && age > 0 ? `${d.title} jarig (${age})` : `${d.title} jarig`;
}

/** What falls on a day: the dates themselves, and the ones whose reminder is that day. */
export function datesOn<T extends YearlyDate>(dates: T[], day: string): { today: T[]; ahead: T[] } {
  const today: T[] = [];
  const ahead: T[] = [];
  for (const d of dates) {
    if (nextOn(d, day) === day) today.push(d);
    else if (d.remind_days_before > 0 && nextOn(d, addDays(day, d.remind_days_before)) === addDays(day, d.remind_days_before))
      ahead.push(d);
  }
  return { today, ahead };
}
